import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import PaymentCollectionApi from 'api/b2b/PaymentCollectionApi'
import B2bTDPAPI from 'api/b2b/TdpApi'
import { downloadExcel } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import PaymentAllocationDetailedList from './PaymentAllocationDetailedList'
import PaymentAllocationList from './PaymentAllocationList'
import { PendingPaymentTableProps } from './PaymentCollectionAdd'
import PaymentDuesList from './PaymentDuesList'
// import PaymentAllocationDetailedList from './PaymentAllocationDetailedList'

export type PaymentCollectionDetailedApiResponse = {
  id: string
  payment_allocation_id: string
  invoice_no: string
  allocation_currency: string
  received_amount: number
  already_received: number
  booking_amount: number
  last_pending_amount: number
  is_active: boolean
  created_at: string
  created_by: string
  order_id: string
  reference_no: string
  product: string
  mode_of_payment: string
}
interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

export type PaymentAllocation = {
  allocation_id: string
  allocation_date: string
  company_name: string
  allocated_amount: number
  reference_number: string
  created_by: string
  created_on: string
  payment_mode: string
  received_amount: number
  last_pending_amount: number
}

export default function PaymentCollectionSearchForm() {
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [tableData, setTableData] = useState<PaymentAllocation[]>([])
  const debouncedSearchTerm = useDebounce(companySearchTerm, 500)
  const [showDetailedView, setShowDetailedView] = useState(false)
  const [showDuesTable, setShowDuesTable] = useState<
    PendingPaymentTableProps[]
  >([])

  // Updated state to match the actual API response structure
  const [paymentCollectionDetailedData, setPaymentCollectionDetailedData] =
    useState<PaymentCollectionDetailedApiResponse[]>([])

  // New state to store the selected row data
  const [selectedRowData, setSelectedRowData] = useState<{
    allocationId: string
    companyName: string
  } | null>(null)

  const token = OrbitSession.get('faberToken')

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      allocationId: '',
      companyName: null,
      allocationDate: null,
      referenceNumber: ''
    }
  })

  // Query for fetching companies based on search term
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  // First API call - using company data
  useQuery({
    queryKey: ['companyDetails', selectedRowData?.companyName],
    queryFn: async () => {
      const params = { company_name: selectedRowData?.companyName || '' }
      const response = await PaymentCollectionApi.getUnpaidPaymentAllocation(
        token,
        params
      )
      return response.data
    },
    enabled: !!selectedRowData?.companyName && showDetailedView,
    onSuccess: (data) => {
      setShowDuesTable([data.wallet_summary])
    }
  })

  // Second API call - using allocation id (updated to handle the new response structure)

  useQuery({
    queryKey: ['allocationDetails', selectedRowData?.allocationId],
    queryFn: async () => {
      const response =
        await PaymentCollectionApi.getUnpaidPaymentAllocationById(
          token,
          selectedRowData?.allocationId || ''
        )
      return response.data // This should match the API response structure
    },
    enabled: !!selectedRowData?.allocationId && showDetailedView,
    onSuccess: (data) => {
      // The API returns the data directly, not wrapped in an array
      if (data.payment_allocation_detail) {
        setPaymentCollectionDetailedData(data.payment_allocation_detail)
      } else {
        setPaymentCollectionDetailedData([])
      }
    }
  })

  // Mutation for searching payment collection
  const {
    mutate: searchPaymentCollection,
    isLoading: isLoadingPaymentCollection
  } = useMutation({
    mutationFn: async (params: Record<string, string>) => {
      return await PaymentCollectionApi.getPaymentCollection(token, params)
    },
    onSuccess: (data) => {
      if (data.data != null && Array.isArray(data.data)) setTableData(data.data)
      else {
        setTableData([])
        toast.info('No data found ', {
          autoClose: 2000,
          hideProgressBar: false
        })
      }
    },
    onError: (error) => {
      console.error('Failed to fetch customer statement:', error)
    }
  })

  // Handle view details action
  const handleViewDetails = useCallback(
    (allocationId: string, companyName: string) => {
      setSelectedRowData({ allocationId, companyName })
    },
    []
  )

  const onSubmit = (data) => {
    const cleanedData: Record<string, string> = {
      company_id: data.companyName?.companyId || ''
    }

    if (data.currency) {
      cleanedData.currency = data.currency
    }
    if (data.modeOfPayment) {
      cleanedData.mode_of_payment = data.modeOfPayment
    }
    if (data.receiptDate) {
      cleanedData.receipt_date = data.receiptDate
    }
    if (data.receiptNumber) {
      cleanedData.receipt_number = data.receiptNumber
    }
    searchPaymentCollection(cleanedData)
  }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const excelDownload = (data: any) => {
    const excelData = {
      fileName: companySearchTerm + '-PaymentCollection',
      CustomerStatement: data
    }
    downloadExcel(excelData)
  }

  const onReset = () => {
    reset()
    setSelectedRowData(null)
    setShowDetailedView(false)
    setShowDuesTable([])
    setPaymentCollectionDetailedData([])
    setTableData([])
  }

  const navigate = useNavigate()

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  // Handle input change for company search
  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  return (
    <Box sx={{ p: 3 }}>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          backgroundColor: '#fff',
          p: 3,
          borderRadius: 2,
          boxShadow: 1,
          mb: 3
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Controller
              name='referenceNumber'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Reference Number'
                  size='small'
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name='allocationId'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Allocation Id'
                  size='small'
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='companyName'
              control={control}
              rules={{
                required: { value: true, message: 'Company name is required' }
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  options={companyOptions}
                  loading={isTenantLoading}
                  getOptionLabel={(option: CompanyOption | null) =>
                    option?.label || ''
                  }
                  isOptionEqualToValue={(
                    option: CompanyOption,
                    value: CompanyOption | null
                  ) => option?.value === value?.value}
                  onChange={(_, data: CompanyOption | null) =>
                    field.onChange(data)
                  }
                  onInputChange={handleCompanyInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Company Name *'
                      variant='outlined'
                      fullWidth
                      size='small'
                      error={!!error}
                      helperText={
                        error?.message ||
                        (debouncedSearchTerm.length > 0 &&
                        debouncedSearchTerm.length < 2
                          ? 'Type at least 2 characters to search'
                          : '')
                      }
                      placeholder='Type company name to search...'
                      InputProps={{
                        ...params.InputProps
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='body1'>{option.label}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          ID: {option.companyId}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : isTenantLoading
                      ? 'Searching...'
                      : tenantFetchError
                      ? 'Error loading companies'
                      : 'No companies found'
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='allocationDate'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type='date'
                  label='Allocation Date'
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  fullWidth
                  size='small'
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}></Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-start',
                pt: 1
              }}
            >
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={isLoadingPaymentCollection}
                sx={{ textTransform: 'none', minWidth: 120 }}
              >
                {isLoadingPaymentCollection ? (
                  <CircularProgress size={20} />
                ) : (
                  'Search'
                )}
              </Button>
              <Button
                variant='outlined'
                color='primary'
                type='button'
                onClick={onReset}
                sx={{ textTransform: 'none', minWidth: 120 }}
              >
                Reset
              </Button>
              <Button
                variant='outlined'
                color='primary'
                type='button'
                onClick={() => excelDownload(tableData)}
                disabled={!tableData?.length}
                sx={{ textTransform: 'none' }}
              >
                Excel Download
              </Button>
              <Button
                variant='contained'
                color='primary'
                type='button'
                onClick={() => navigate('/hq/b2b/payment-collection/add')}
                sx={{ textTransform: 'none' }}
              >
                Add Payment Collection
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box>
        <PaymentAllocationList
          data={tableData}
          setShowDetailedView={setShowDetailedView}
          onViewDetails={handleViewDetails}
        />
      </Box>

      <Box>
        <PaymentDuesList data={showDuesTable} />
      </Box>

      <Box>
        {/* Pass the single object, not an array */}
        <PaymentAllocationDetailedList
          data={paymentCollectionDetailedData}
          // isLoading={isAllocationApiLoading}
        />
      </Box>
    </Box>
  )
}
